/**
 * civilRespondCheck.tsx — The check flow for civil respondents.
 *
 * Single page that handles all 3 check types via a route param:
 *   /civil-respondent/check/:targetType
 *
 *   targetType: missing_person | most_wanted | stolen_vehicle
 *
 * Flow:
 *   1. User lands on the page
 *   2. Camera (or file picker) is shown in a square
 *   3. User takes a snapshot
 *   4. Image is sent to /api/civil-respondent/check
 *   5. Result is shown with the matched record (if any)
 *   6. "False Positive" button to dismiss
 *   7. "Take Another" to reset
 *
 * v0.4.9.3.4c2 — two changes:
 *   1. The CropperAspect type is no longer re-exported here. Vite's
 *      isolatedModules requires type-only exports to use the `type`
 *      keyword, and this file never used it anyway. (Friend's review.)
 *   2. SuperAdmin / SAdmin fail-fast on the Run Check button.
 *
 *      The previous behaviour: a sadmin/superadmin landing on this page
 *      without `?orgId=` in the URL and without `civilRespond_actAsOrgId`
 *      in localStorage would take a photo, tap Run Check, and get a 400
 *      `organization_id required` from the backend AFTER the photo was
 *      already on disk. The error surfaced as `err.message` and the
 *      button stayed enabled — so the admin kept tapping it and kept
 *      getting 400s.
 *
 *      The fix: read the user via `useAuth()` and disable Run Check
 *      (with a banner pointing to the org picker) up front. The
 *      `orgIdFromQuery` fallback chain in the caller is unchanged for
 *      non-admin users — agents don't need to pick an org because their
 *      own org is implicit.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { useTranslation } from 'react-i18next';
import { Camera, X, Crop as CropIcon } from 'lucide-react';
import ImageCropper from '../components/ImageCropper';
import ResultDisplay from '../components/ResultDisplay';

type TargetType = 'missing_person' | 'most_wanted' | 'stolen_vehicle';

export default function CivilRespondCheck() {
  const { targetType } = useParams<{ targetType: string }>();
  const [searchParams] = useSearchParams();
  // Admin "act as" org id. Comes from URL ?orgId=... (set by civilRespond.tsx
  // when a sadmin/superadmin picks an org to test) or localStorage fallback.
  const orgIdFromQuery = searchParams.get('orgId') || localStorage.getItem('civilRespond_actAsOrgId') || undefined;
  const navigate = useNavigate();
  const { t } = useTranslation();
  // v0.4.9.3.4c2: pull user in so we can fail fast for admins who haven't
  // picked an org. `useAuth` throws if used outside `<AuthProvider>`; the
  // App-level guard makes that impossible in production, and the harness
  // stubs `useAuth` to return `{}` so the user lookup is just `undefined`.
  const { user } = useAuth();
  const userRole = (user as any)?.role as string | undefined;
  const isAdmin = userRole === 'superadmin' || userRole === 'sadmin';
  const needsOrg = isAdmin && !orgIdFromQuery;
  const canRunCheck = !needsOrg;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [snapshot, setSnapshot] = useState<string | null>(null);
  // Image awaiting cropping (set right after capture/upload, before the cropper confirms).
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [camerasUnsupported, setCamerasUnsupported] = useState(false);

  const isPlateCheck = targetType === 'stolen_vehicle';

  // Start the camera on mount, stop on unmount.
  // Always use the rear camera (environment). The agent points the phone
  // at the subject — for both face checks and plate checks.
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamerasUnsupported(true);
      return;
    }
    let cancelled = false;
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
      audio: false,
    })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = s;
        setStream(s);
      })
      .catch((err) => {
        setError(`Camera unavailable: ${err.message}`);
        setCamerasUnsupported(true);
      });
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // v0.4.9.3.2: attach the stream through a CALLBACK REF, not a useRef.
  //
  // Showing a result unmounts the whole camera block — the <video> element
  // included — because of the `{!result ? ... : ...}` conditional below. On
  // "Take another" a brand-new <video> is created. React state still holds the
  // MediaStream, but nothing put it on the NEW element, and the acquisition
  // effect above has `[]` deps so it never re-runs. The result is a black
  // camera that only recovers after leaving the page and coming back.
  //
  // A callback ref is invoked on every mount and unmount of the element, so it
  // covers the remount. Its identity also changes when `stream` arrives, and
  // React re-invokes a ref whose identity changed — so this single callback
  // covers BOTH "the element appeared" and "the stream finally arrived".
  //
  // A `useEffect` keyed on [stream] does NOT work: the element remounts while
  // `stream` is unchanged, so the effect never re-runs and the camera stays
  // dead. That was tried first and the render test caught it.
  //
  // The explicit play() matters in the Android WebView, where re-attaching a
  // stream to a fresh element does not always resume playback on its own.
  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    if (!el || !stream) return;
    if (el.srcObject !== stream) {
      el.srcObject = stream;
    }
    const started = el.play();
    if (started && typeof started.catch === 'function') {
      started.catch(() => { /* autoplay policy; the stream is attached regardless */ });
    }
  }, [stream]);

  function reset() {
    setSnapshot(null);
    setResult(null);
    setError(null);
    setCropSource(null);
  }

  function takeSnapshot() {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCropSource(dataUrl);
  }

  async function runCheck() {
    if (!snapshot) return;
    setChecking(true);
    setError(null);
    setResult(null);
    /*  logging block to remove */
    try {
      const img = new Image();
      img.onload = () => {
        console.log(`[FRONTEND] Sending image: ${img.width}x${img.height}`);
      };
      img.src = snapshot;
    } catch (e) {
      console.warn('Could not log image dimensions', e);
    }
    /*  end of logging block */
    try {
      const r = await api.runCivilCheck(snapshot, targetType as TargetType, orgIdFromQuery);
      setResult(r);
    } catch (err: any) {
      setError(err.message || t('civil.check.err.failed'));
    } finally {
      setChecking(false);
    }
  }

  if (!targetType || !['missing_person', 'most_wanted', 'stolen_vehicle'].includes(targetType)) {
    return <div className="p-6">{t('civil.check.invalidType')}</div>;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top bar */}
      <header className="px-4 py-3 bg-card border-b border-border flex items-center justify-between">
        <button
          onClick={() => navigate('/civil-respondent')}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          <X className="w-5 h-5" /> {t('common.back')}
        </button>
        <h1 className="text-base font-semibold text-foreground">{getCheckTitle(targetType as TargetType, t)}</h1>
        <div className="w-12" />
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-4 max-w-xl mx-auto w-full">
        {!result ? (
          <>
            {/* Camera/snapshot view.

                The <video> is hidden with CSS rather than unmounted, so the
                stream survives snapshot → retake within this view.

                It IS unmounted when a result is shown (see the `!result`
                conditional), which is why the stream-keyed effect above has
                to re-attach it on the way back. Do not "simplify" that
                effect away. */}
            <div className="w-full aspect-square bg-black rounded-2xl overflow-hidden relative shadow-xl">
              {/* Live video (always mounted). Hidden when a snapshot is shown. */}
              <video
                ref={attachVideo}
                autoPlay
                playsInline
                muted
                className={`absolute inset-0 w-full h-full object-cover transition-opacity ${snapshot ? 'opacity-0' : 'opacity-100'}`}
              />
              {snapshot ? (
                <img src={snapshot} alt={t('civil.check.alt.snapshot')} className="absolute inset-0 w-full h-full object-contain" />
              ) : camerasUnsupported ? (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm p-4 text-center">
                  {t('civil.check.cameraUnavailable')}
                </div>
              ) : null}
              <canvas ref={canvasRef} className="hidden" />
            </div>

            {/* Hidden file input fallback for desktop / camera-unavailable.
                No `capture` attribute — on mobile this opens the file picker
                (gallery + camera option) instead of forcing the camera.
                Gallery photos are resized client-side to 1280px max before
                sending (matches plate_detector.py's _MAX_DIM). */}
            <input
              id="file-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const resized = await resizeImage(file, 1280);
                  setCropSource(resized);
                } catch (err) {
                  console.warn('resize failed, using original:', err);
                  const reader = new FileReader();
                  reader.onload = () => setCropSource(reader.result as string);
                  reader.readAsDataURL(file);
                }
                // reset so the same file can be re-selected
                e.target.value = '';
              }}
            />

            {/* Controls */}
            <div className="mt-6 flex flex-col items-center gap-3 w-full">
              {!snapshot ? (
                <>
                  <button
                    onClick={takeSnapshot}
                    disabled={!stream || camerasUnsupported}
                    className="w-16 h-16 rounded-full bg-primary hover:bg-primary/90 disabled:bg-muted text-white shadow-lg flex items-center justify-center transition"
                  >
                    <Camera className="w-7 h-7" />
                  </button>
                  <p className="text-xs text-muted-foreground">
                    {t('civil.check.tapToTake')}{isPlateCheck ? t('civil.check.ofLicensePlate') : t('civil.check.ofPerson')}
                  </p>
                  <button
                    onClick={() => document.getElementById('file-input')?.click()}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {t('civil.check.orUpload')}
                  </button>
                </>
              ) : (
                <div className="flex gap-3 w-full">
                  <button
                    onClick={reset}
                    disabled={checking}
                    className="flex-1 py-3 rounded-lg border border-input text-foreground hover:bg-background disabled:opacity-50"
                  >
                    {t('civil.check.retake')}
                  </button>
                  {/* v0.4.9.3.4: RESTORED. This was commented out while chasing
                      the 18% watermark strip, but it turned out to be unrelated
                      to that and genuinely useful on its own:

                        - motorcycle plates are SQUARE with the characters in two
                          rows, and a 3:1 crop cuts a square plate into a strip
                          before the server ever sees it;
                        - a tight re-crop is the fastest fix for a shot where the
                          plate is small in frame or the border is included.

                      It opens the cropper on the CURRENT snapshot, so a
                      confirmed re-crop replaces it rather than needing a new
                      shot from the camera. */}
                  <button
                    onClick={() => setCropSource(snapshot)}
                    disabled={checking}
                    className="flex-1 py-3 rounded-lg border border-input text-foreground hover:bg-background disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <CropIcon className="w-4 h-4" /> {t('civil.check.crop')}
                  </button>
                  <button
                    onClick={runCheck}
                    disabled={checking || !canRunCheck}
                    className="flex-1 py-3 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium disabled:opacity-50"
                  >
                    {checking ? t('civil.check.checking') : t('civil.check.runCheck')}
                  </button>
                </div>
              )}

              {error && (
                <div className="w-full mt-2 p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded text-sm">
                  {error}
                </div>
              )}
            </div>

            {/* v0.4.9.3.4c2: superadmin/sadmin must pick an org first. The
                Run Check button above is also disabled while this banner is
                shown, so the admin can't waste a photo on a 400. */}
            {needsOrg && snapshot && (
              <div className="w-full mt-2 p-3 bg-amber-100 border border-amber-300 text-amber-900 rounded text-sm">
                <div className="font-medium mb-1">
                  {t('civil.check.pickOrgFirstTitle') || 'Pick an organisation first'}
                </div>
                <div className="mb-2">
                  {t('civil.check.pickOrgFirstBody') ||
                    'As an admin, choose which civil-respondent organisation to act as before running a check.'}
                </div>
                <button
                  onClick={() => navigate('/civil-respondent')}
                  className="text-sm font-medium underline"
                >
                  {t('civil.check.pickOrgFirstCta') || 'Go to org picker'}
                </button>
              </div>
            )}
          </>
        ) : (
          <ResultDisplay
            result={result}
            targetType={targetType as TargetType}
            onAnother={reset}
            onBack={() => navigate('/civil-respondent')}
          />
        )}
      </main>

      {/* Cropper modal — shows after capture or upload, before sending. */}
      {cropSource && (
        <ImageCropper
          imageDataUrl={cropSource}
          aspect={isPlateCheck ? '3:1' : '1:1'}
          onConfirm={(cropped) => {
            setSnapshot(cropped);
            setCropSource(null);
          }}
          onCancel={() => setCropSource(null)}
        />
      )}
    </div>
  );
}

// ResultDisplay was extracted to src/components/ResultDisplay.tsx in v0.4.9.3.5
// to add the Edit button, FP form, and image-above-result. The new file
// owns the result card, flip-card image, and the Edit/FP correction flow.
// `Field` (the small label/value row) is also defined there.

/**
 * Resize an image File to a data URL with a max-dimension constraint.
 * Used to downscale gallery photos before sending — the server-side
 * plate_detector.py resizes to 1280px anyway, so doing it here saves
 * bandwidth and stays under the 100KB JSON body-parser default.
 */
async function resizeImage(file: File, maxDim: number): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });
  const { width, height } = img;
  if (Math.max(width, height) <= maxDim) {
    return dataUrl; // already small enough
  }
  const scale = maxDim / Math.max(width, height);
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d not available');
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.85);
}

// Helper kept at module level so Vite/Rollup's minifier doesn't lose the
// `t` binding when destructuring from useTranslation() inside a const
// initializer inside the component body.
function getCheckTitle(targetType: TargetType, t: (key: string) => string): string {
  if (targetType === 'missing_person') return t('civil.missing.checkTitle');
  if (targetType === 'most_wanted') return t('civil.mostWanted.checkTitle');
  return t('civil.stolenVehicles.checkTitle');
}
